import { apiRequest } from '@/src/services';
import { getImageBlob, releaseImageRef, Constants } from '@/src/utils';
import {
    type ChoiceDTO,
    type MatrixRow,
    type MatrixColumn,
    type MatrixCell,
    type DragChoice,
} from '@/src/lib';
import { defaultMeta } from '../models/questionDTOs';

const qtm = Constants.QUESTION_TYPE_MASTER_IDS;

function extractImages(html: string, requiresTag: boolean = false): string[] {
    // Matches src="local://[anything ending with .png/.jpg/.jpeg/.gif]"
    // If plainString is true, matches local://[anything...]
    const results: string[] = [];

    // Match local:// images
    const localRegex = requiresTag
        ? /src\s*=\s*"(local:\/\/[a-zA-Z0-9-]+(?:\.(?:png|jpg|jpeg|gif)))"/gi
        : /(local:\/\/[a-zA-Z0-9-]+(?:\.(?:png|jpg|jpeg|gif)))/gi;
    let match;
    while ((match = localRegex.exec(html)) !== null) {
        results.push(match[1]);
    }

    return results;
}

function appendFormData(formData: FormData, data: any, parentKey = '') {
    if (data === null || data === undefined) {
        return;
    }

    if (Array.isArray(data)) {
        data.forEach((value, index) => {
            const key = parentKey ? `${parentKey}[${index}]` : `[${index}]`;
            appendFormData(formData, value, key);
        });
    } else if (typeof data === 'object') {
        Object.keys(data).forEach((key) => {
            const value = data[key];
            const newKey = parentKey ? `${parentKey}.${key}` : key;
            appendFormData(formData, value, newKey);
        });
    } else {
        formData.append(parentKey, data);
    }
}

export async function saveQuestion(question: any, mode: 'create' | 'edit'): Promise<any> {
    const url =
        mode === 'create' ? '/assessments/questions' : `/assessments/questions/${question.id}`;
    const method = mode === 'create' ? 'post' : 'put';

    const formData = new FormData();

    const imagesToProcess = extractImages(question.prompt || '');
    imagesToProcess.push(...extractImages(question.prompt_es || ''));
    switch (question.meta.questionTypeMasterId) {
        case qtm.MULTIPLE_CHOICE:
            question.choices.forEach((choice: ChoiceDTO) => {
                imagesToProcess.push(...extractImages(choice.text));
                imagesToProcess.push(...extractImages(choice.text_es));
            });
            break;
        case qtm.OPEN_TEXT:
            question.correctAnswers.forEach((choice: ChoiceDTO) => {
                imagesToProcess.push(...extractImages(choice.text));
                imagesToProcess.push(...extractImages(choice.text_es));
            });
            break;
        case qtm.MULTIPLE_SELECT:
            question.choices.forEach((choice: ChoiceDTO) => {
                imagesToProcess.push(...extractImages(choice.text));
                imagesToProcess.push(...extractImages(choice.text_es));
            });
            break;
        case qtm.MATRIX:
            question.rows.forEach((row: MatrixRow) => {
                imagesToProcess.push(...extractImages(row.text));
                imagesToProcess.push(...extractImages(row.text_es));
            });
            question.columns.forEach((col: MatrixColumn) => {
                imagesToProcess.push(...extractImages(col.text));
                imagesToProcess.push(...extractImages(col.text_es));
            });
            question.cells.forEach((cell: MatrixCell) => {
                imagesToProcess.push(...extractImages(cell.staticContent || ''));
                imagesToProcess.push(...extractImages(cell.staticContent_es || ''));
            });
            if (question.additionalMeta.excludeHeaderRow) {
                question.columns.forEach((col: MatrixColumn) => {
                    col.text = '-';
                    col.text_es = '-';
                });
            }
            if (question.additionalMeta.excludeHeaderColumn) {
                question.rows.forEach((row: MatrixRow) => {
                    row.text = '-';
                    row.text_es = '-';
                });
            }
            break;
        case qtm.HOTSPOT:
            [question.backgroundImageUrl, question.backgroundImageUrl_es].forEach((url) => {
                imagesToProcess.push(...extractImages(url || ''));
            });
            break;
        case qtm.DRAG_AND_DROP:
            [question.backgroundImageUrl, question.backgroundImageUrl_es].forEach((url) => {
                imagesToProcess.push(...extractImages(url || ''));
            });
            question.choices.forEach((choice: DragChoice) => {
                imagesToProcess.push(...extractImages(choice.value, false));
                imagesToProcess.push(...extractImages(choice.value_es, false));
            });
            break;
        case qtm.STIMULUS:
            question.meta = {
                ...defaultMeta,
                isSpanishAllowed: question.meta.isSpanishAllowed || false,
                questionTypeMasterId: qtm.STIMULUS,
                gradeLevelMasterIds: [1],
            };
            break;
        case qtm.GRAPHING:
            break;
        default:
            throw 'Invalid question type';
    }

    appendFormData(formData, question);

    for (const url of imagesToProcess) {
        const fileName = url.replace('local://', '');
        const blob = getImageBlob(fileName);
        if (!blob) continue;

        formData.append('images', blob, fileName);
    }
    if (imagesToProcess.length === 0) {
        formData.append('images', '');
    }

    const apiHeaders = { 'Content-Type': 'multipart/form-data' };
    const response = await apiRequest(method, url, formData, apiHeaders);
    // Clear up memory after upload
    for (const fileName of imagesToProcess) {
        releaseImageRef(fileName);
    }
    return response.data;
}
